import { useCallback, useEffect, useRef, useState } from 'react';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import {
  BimMeasureMode,
  BimMeasurement,
  BimSelection,
  BimTreeNode,
  BimViewerHandle,
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { createBimViewerAdapter } from 'Data/adapters/BimViewer';
import { COORDLY_3D_VIEWER_EXTENSION } from 'Utils/ViewerUtils';
import { IProperties } from 'Data/interfaces/FederatedViewer/IProperties';
import { resolveUrnToFile, fileToUrl } from '../../federationUrn';
import { bimEntityToProperties } from '../../ifcProperties';

interface IUseFederatedIfcViewer {
  viewerDiv: HTMLElement | null;
  urns: string[];
  viewerListDisciplines: IDisciplineViewerList[];
  constructionSiteId: number;
  onEngineError?(detail: { code: string; message: string }): void;
  // O modelo não entrou na cena. O evento de erro do motor não diz de qual
  // modelo se trata, mas aqui a identidade é conhecida — sem isso o urn segue em
  // `urns` e o switch fica ligado sem nada na cena.
  onModelLoadFailed?(urn: string): void;
  // Urn em carga, ou null quando o motor está livre. A lista usa isso para
  // travar os switches durante a carga — o equivalente ao `loadUrn` da Autodesk.
  onLoadingUrnChange?(urn: string | null): void;
  // Propriedades do elemento selecionado, já no formato do painel do federado.
  // `null` quando a seleção é limpa.
  onSelectedProperties?(properties: IProperties | null): void;
  // Árvore espacial de UM modelo, quando o data model dele fica pronto (chega
  // depois do render). A lista desenha uma árvore por modelo ligado.
  onModelTree?(urn: string, tree: BimTreeNode[], modelIndex?: number): void;
  // O handle é o que a árvore usa para selecionar no 3D e materializar folhas.
  // `null` no unmount.
  onHandleReady?(handle: BimViewerHandle | null): void;
  // Repasses crus do motor para o `useBim3DTools`, que mantém o estado das
  // ferramentas (isolar, ocultar, corte, medição). O hook daqui continua
  // cuidando só da cena federada.
  onSelection?(selection: BimSelection): void;
  onMeasure?(state: { mode: BimMeasureMode; measurements: BimMeasurement[] }): void;
}

// Hook do viewer IFC federado (paralelo ao useInternalViewer, mas p/ ifc-lite).
// Monta a cena vazia via porta e adiciona um modelo por urn IFC ativo. Não muta
// Redux — só lê `urns`/`viewerListDisciplines`. A porta isola o motor.
export const useFederatedIfcViewer = ({
  viewerDiv,
  urns,
  viewerListDisciplines,
  constructionSiteId,
  onEngineError,
  onModelLoadFailed,
  onLoadingUrnChange,
  onSelectedProperties,
  onModelTree,
  onHandleReady,
  onSelection,
  onMeasure,
}: IUseFederatedIfcViewer) => {
  const handleRef = useRef<BimViewerHandle | null>(null);
  const loadedRef = useRef<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'download' | 'parse'>('idle');

  // Refs p/ o processUrns ficar estável (sem re-montar quando a lista muda).
  const urnsRef = useRef(urns);
  urnsRef.current = urns;
  const dataRef = useRef({ viewerListDisciplines, constructionSiteId });
  dataRef.current = { viewerListDisciplines, constructionSiteId };
  const onErrorRef = useRef(onEngineError);
  onErrorRef.current = onEngineError;
  const onModelFailedRef = useRef(onModelLoadFailed);
  onModelFailedRef.current = onModelLoadFailed;
  const onLoadingRef = useRef(onLoadingUrnChange);
  onLoadingRef.current = onLoadingUrnChange;
  const onPropertiesRef = useRef(onSelectedProperties);
  onPropertiesRef.current = onSelectedProperties;
  const onModelTreeRef = useRef(onModelTree);
  onModelTreeRef.current = onModelTree;
  const onHandleReadyRef = useRef(onHandleReady);
  onHandleReadyRef.current = onHandleReady;
  const onSelectionRef = useRef(onSelection);
  onSelectionRef.current = onSelection;
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;

  // Data model de um modelo ficou pronto: lê a árvore DELE (por `modelIndex`) e
  // entrega para a lista. Sem `modelId` não dá para saber a qual switch a árvore
  // pertence, então não há o que fazer.
  const handleDataModelReady = useCallback((detail: {
    available: boolean;
    modelIndex?: number;
    modelId?: string;
  }) => {
    const handle = handleRef.current;
    if (!handle || !detail.modelId) { return; }
    onModelTreeRef.current?.(
      detail.modelId,
      detail.available ? handle.getSpatialTree(detail.modelIndex) : [],
      detail.modelIndex,
    );
  }, []);

  // Clique na cena → propriedades do elemento. O motor manda só a seleção; quem
  // lê os dados é a porta, e só depois do data model chegar (ele vem depois do
  // render). Sem elemento selecionado o painel volta a vazio.
  const handleSelectionChanged = useCallback((selection: BimSelection) => {
    onSelectionRef.current?.(selection);
    const handle = handleRef.current;
    if (!handle || selection.expressId === null) {
      onPropertiesRef.current?.(null);
      return;
    }
    // `modelIndex` é obrigatório aqui: o expressId é local ao modelo, então sem
    // ele a leitura cairia no data model errado quando há vários federados.
    onPropertiesRef.current?.(
      bimEntityToProperties(
        handle.getEntityProperties(selection.expressId, selection.modelIndex),
      ),
    );
  }, []);

  // Reconcilia a cena com os urns ativos: tira os que saíram, põe os que
  // entraram. Simétrico — ligar e desligar custam o mesmo, e cada modelo é
  // independente dos outros.
  //
  // Antes isto era `clearModels()` + re-add de TODOS os restantes, porque a cena
  // não sabia remover por modelo (`removeMeshesForEntities` é por expressId, e
  // disciplinas reusam ids). Além do custo — desligar 1 de N re-baixava e
  // re-parseava os N-1 —, a janela entre o clear e os re-adds deixava a cena e o
  // `loadedRef` divergirem quando um re-add falhava ou o usuário mexia em outro
  // switch no meio: o modelo desligado voltava, ou sumia o errado. O motor agora
  // dá faixa de id própria a cada modelo e remove só o dele (`removeModel`).
  const runUrns = useCallback(async () => {
    const handle = handleRef.current;
    if (!handle) { return; }
    const { viewerListDisciplines: disciplines, constructionSiteId: csId } = dataRef.current;

    const active = new Set(urnsRef.current);
    for (const urn of Array.from(loadedRef.current)) {
      if (active.has(urn)) { continue; }
      handle.removeModel(urn);
      loadedRef.current.delete(urn);
      // A árvore sai com o modelo: mantê-la desenhada apontaria para geometria
      // que não está mais na cena.
      onModelTreeRef.current?.(urn, []);
    }

    for (const urn of urnsRef.current) {
      if (loadedRef.current.has(urn)) { continue; }
      const file = resolveUrnToFile(urn, disciplines);
      if (!file || file.Extension?.toLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) { continue; }
      // Marca ANTES do await pra não duplicar numa segunda passada enquanto esta
      // carrega; se falhar, desmarca — senão o urn nunca mais seria tentado.
      loadedRef.current.add(urn);
      // Só avisa se o urn ainda estiver ativo: desligado no meio da carga, o
      // switch já saiu por ação do usuário e avisar aqui o religaria (SET_URNS
      // é toggle).
      const reportFailure = () => {
        if (urnsRef.current.includes(urn)) { onModelFailedRef.current?.(urn); }
      };

      // Trava os switches enquanto este modelo carrega. Sem isso dá para ligar
      // vários de uma vez (downloads e parses concorrentes) e para ligar/desligar
      // o mesmo várias vezes durante a carga — o efeito só aparecia no fim, na
      // última ação que tivesse sobrado.
      onLoadingRef.current?.(urn);
      try {
        let added = true;
        try {
          await handle.addModel({ fileUrl: fileToUrl(file, csId), fileName: file.Name }, urn);
        } catch {
          added = false;
        }

        // O motor engole a falha de carga (reporta por onError e desiste do
        // modelo), então a promise resolver não significa que entrou na cena.
        if (!added || !handle.hasModel(urn)) {
          loadedRef.current.delete(urn);
          reportFailure();

        // Desligado DURANTE a carga: esta passada já passou da fase de remoção, e
        // a próxima só roda se `urns` mudar de novo — então desfaz aqui.
        } else if (!urnsRef.current.includes(urn)) {
          handle.removeModel(urn);
          loadedRef.current.delete(urn);
          onModelTreeRef.current?.(urn, []);
        }
      } finally {
        onLoadingRef.current?.(null);
      }
    }
  }, []);

  // Serializa as passadas: um clear disparado no meio de um addModel em voo
  // deixaria a cena inconsistente (o modelo em carga sobreviveria ao reset).
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const processUrns = useCallback(() => {
    queueRef.current = queueRef.current.then(runUrns, runUrns);
    return queueRef.current;
  }, [runUrns]);

  useEffect(() => {
    if (!viewerDiv) { return; }
    let cancelled = false;

    const adapter = createBimViewerAdapter();
    adapter
      .mount(viewerDiv, {}, {
        onLoadProgress: (p) => setPhase(p === 'download' ? 'download' : 'parse'),
        onLoaded: () => { setPhase('idle'); setLoaded(true); },
        onError: (detail) => { setPhase('idle'); onErrorRef.current?.(detail); },
        onSelectionChanged: handleSelectionChanged,
        onDataModelReady: handleDataModelReady,
        onMeasureChanged: (state) => onMeasureRef.current?.(state),
      })
      .then((handle) => {
        if (cancelled) { handle.dispose(); return; }
        handleRef.current = handle;
        onHandleReadyRef.current?.(handle);
        void processUrns(); // urns já ativos ao montar
      })
      .catch((err) => {
        onErrorRef.current?.({ code: 'code-load-failed', message: String(err?.message ?? err) });
      });

    return () => {
      cancelled = true;
      handleRef.current?.dispose();
      handleRef.current = null;
      onHandleReadyRef.current?.(null);
      loadedRef.current.clear();
    };
  }, [viewerDiv, processUrns, handleSelectionChanged, handleDataModelReady]);

  // Novos urns ativados depois do mount.
  useEffect(() => { void processUrns(); }, [urns, processUrns]);

  const fitToView = useCallback(() => handleRef.current?.fitToView(), []);

  // `handleRef` sai daqui para o `useBim3DTools`, que opera a cena pelos mesmos
  // comandos da porta — é o que permite reusar as ferramentas do viewer único
  // sem duplicar estado.
  return { loaded, phase, fitToView, handleRef };
};
